import { useAuth } from "AuthProvider";
import { RoutePaths } from "enums/RoutePaths";
import { Formik, Form as FormikForm } from "formik";
import useFetch from "hooks/useFetch";
import useTvApi, { messageOf } from "hooks/useTvApi";
import TvTargetPicker, { useTargetableTvs } from "pages/TvPage/TvTargetPicker";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  FormikTextarea,
  FormikTextField,
  Icon,
  IconButton,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  Toggle,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import {
  Poster,
  TvAlertKey,
  TvAlertMinutes,
  TvSettings,
} from "types/TvPosterTypes";
import * as Yup from "yup";
import PosterIcon from "./PosterIcons";
import styles from "./PosterPage.module.scss";
import DailyPostersPanel from "./DailyPostersPanel";
import TvOrderPanel from "./TvOrderPanel";
import {
  ALERT_FIELDS,
  applyOrder,
  DEFAULT_ALERT_MINUTES,
  DEFAULT_SLIDE_SECONDS,
  fullOrder,
  normalizeAlertMinutes,
  normalizeSeconds,
  tvOrder,
} from "./tvOrder";

interface PosterForm {
  tittel: string;
  tekst: string;
  plakat: File | null;
  activated: boolean;
  activatedForTv: boolean;
  priority?: number;
}

type PosterFilter = "all" | "app" | "tv";

const validationSchema = Yup.object().shape({
  tittel: Yup.string().required("Tittel er påkrevd"),
  tekst: Yup.string().required("Tekst er påkrevd"),
});

const pad = (n: number) => String(n).padStart(2, "0");

/** «yyyy-MM-dd HH:mm», som plakat-endepunktene forventer. */
const toStamp = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;

/**
 * Plakatens egen dato i «yyyy-MM-dd HH:mm». Backend avviser ISO-datoer
 * («2026-10-09T15:37:00») på PATCH, så de gjøres om. Faller tilbake til nå.
 */
export const posterStamp = (date?: string | null) => {
  const match = date?.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) return `${match[1]} ${match[2]}`;
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) return `${date} 00:00`;
  const parsed = date ? new Date(date) : null;
  return toStamp(parsed && !isNaN(parsed.getTime()) ? parsed : new Date());
};

/** «all» = egen menyside «Plakater» (app + TV), «tv» = fanen under «TV». */
export type PostersPageMode = "all" | "tv";

interface PostersPageProps {
  mode?: PostersPageMode;
}

const PRIORITIES = Array.from({ length: 100 }, (_, index) => index + 1);

/** Options 1–100, plus the current value if it is outside that range. */
const priorityOptions = (current?: number) =>
  current !== undefined && !PRIORITIES.includes(current)
    ? [current, ...PRIORITIES]
    : PRIORITIES;

interface ImageDropProps {
  label: string;
  required?: boolean;
  preview: string | null;
  inputRef?: React.Ref<HTMLInputElement>;
  onFile: (
    file: File | null,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
}

/** File picker styled as the design's drop zone. */
const ImageDrop: React.FC<ImageDropProps> = ({
  label,
  required,
  preview,
  inputRef,
  onFile,
}) => {
  const [over, setOver] = useState(false);
  return (
    <div className={ui.fld}>
      <span className={ui.lbl}>
        {label}
        {required && <span className={ui.req}> *</span>}
      </span>
      <label
        className={`${styles.drop} ${over ? styles.dropOver : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file && file.type.startsWith("image/")) onFile(file);
        }}
      >
        <input
          name="plakat"
          type="file"
          accept="image/*"
          className={styles.fileInput}
          ref={inputRef}
          onChange={(event) =>
            onFile(event.currentTarget.files?.[0] || null, event)
          }
        />
        <span className={styles.dropThumb}>
          {preview ? (
            <img src={preview} alt="Forhåndsvisning" />
          ) : (
            <PosterIcon name="image" size={22} strokeWidth={1.8} />
          )}
        </span>
        <span className={styles.dropText}>
          <b>
            Dra et bilde hit eller{" "}
            <span className={styles.dropLink}>velg fil</span>
          </b>
          <span className={styles.help}>
            JPG eller PNG. Forhåndsvises før lagring.
          </span>
        </span>
      </label>
    </div>
  );
};

const PosterPage: React.FC<PostersPageProps> = ({ mode = "tv" }) => {
  const isAll = mode === "all";
  const { selectedOrgNumber } = useAuth();
  const { isLoading, error, execute } = useFetch<any>();
  const {
    data: posterData,
    isLoading: isPostersLoading,
    execute: fetchPosters,
  } = useFetch<Poster[]>();
  const { execute: updatePoster } = useFetch<any>();
  const api = useTvApi();
  const tvs = useTargetableTvs(selectedOrgNumber);
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [posters, setPosters] = useState<Poster[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [selectedPoster, setSelectedPoster] = useState<Poster | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPoster, setEditingPoster] = useState<Poster | null>(null);
  const [filter, setFilter] = useState<PosterFilter>("all");
  const [slideSeconds, setSlideSeconds] = useState(DEFAULT_SLIDE_SECONDS);
  const [savingSeconds, setSavingSeconds] = useState(false);
  const [alertMinutes, setAlertMinutes] = useState<TvAlertMinutes>(
    DEFAULT_ALERT_MINUTES,
  );
  const [savingAlerts, setSavingAlerts] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchPosters(
        `/api/v1/poster/org/${selectedOrgNumber}`,
        "GET",
      );
      if (response) setPosters(response);
    };
    fetchData();
  }, [selectedOrgNumber]);

  // «Tid per plakat» og «Varselskjermer på TV» for moskeens TV-er
  // (standardverdier hvis de mangler).
  useEffect(() => {
    if (!selectedOrgNumber) return;
    let cancelled = false;
    api<TvSettings>(`/api/v1/mosques/${selectedOrgNumber}/tv-settings`, "GET")
      .then((res) => {
        if (cancelled) return;
        setSlideSeconds(normalizeSeconds(res?.slideSeconds));
        setAlertMinutes(normalizeAlertMinutes(res));
      })
      .catch(() => {
        if (cancelled) return;
        setSlideSeconds(DEFAULT_SLIDE_SECONDS);
        setAlertMinutes(DEFAULT_ALERT_MINUTES);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOrgNumber, api]);

  const handleSlideSeconds = async (seconds: number) => {
    const previous = slideSeconds;
    setSlideSeconds(seconds);
    setSavingSeconds(true);
    try {
      const res = await api<TvSettings>(
        `/api/v1/mosques/${selectedOrgNumber}/tv-settings`,
        "PUT",
        { slideSeconds: seconds },
      );
      if (typeof res?.slideSeconds === "number")
        setSlideSeconds(normalizeSeconds(res.slideSeconds));
      toast.success(`Hver plakat vises i ${seconds} sekunder.`);
    } catch (e) {
      setSlideSeconds(previous);
      toast.error(messageOf(e));
    } finally {
      setSavingSeconds(false);
    }
  };

  /**
   * Én varselskjerm endret. Sender alle innstillingene (også tid per plakat),
   * så en eldre server som bare kjenner `slideSeconds` ikke nullstiller den.
   */
  const handleAlertMinutes = async (key: TvAlertKey, minutes: number) => {
    const previous = alertMinutes;
    const next = { ...alertMinutes, [key]: minutes };
    setAlertMinutes(next);
    setSavingAlerts(true);
    try {
      const res = await api<TvSettings>(
        `/api/v1/mosques/${selectedOrgNumber}/tv-settings`,
        "PUT",
        { slideSeconds, ...next },
      );
      if (res && typeof res[key] === "number")
        setAlertMinutes(normalizeAlertMinutes(res));
      const label = ALERT_FIELDS.find((f) => f.key === key)?.label ?? "";
      toast.success(`«${label}» vises i ${minutes} min på TV.`);
    } catch (e) {
      setAlertMinutes(previous);
      toast.error(messageOf(e));
    } finally {
      setSavingAlerts(false);
    }
  };

  /**
   * Ny rekkefølge for TV-plakatene (dra og slipp). Lagres straks med hele
   * id-rekkefølgen; ved feil rulles prioritetene tilbake.
   */
  const handleReorder = async (tvIds: number[]) => {
    const before = new Map(posters.map((p) => [p.id, p.priority]));
    const ids = fullOrder(posters, tvIds);
    setPosters((prev) => applyOrder(prev, ids));
    try {
      const res = await api<Poster[]>(
        `/api/v1/mosques/${selectedOrgNumber}/posters/order`,
        "PUT",
        { posterIds: ids },
      );
      if (Array.isArray(res) && res.length > 0) {
        const saved = new Map(res.map((p) => [p.id, p.priority]));
        setPosters((prev) =>
          prev.map((p) =>
            saved.has(p.id) ? { ...p, priority: saved.get(p.id)! } : p,
          ),
        );
      }
      toast.success("Rekkefølgen på TV er lagret.");
    } catch (e) {
      setPosters((prev) =>
        prev.map((p) =>
          before.has(p.id) ? { ...p, priority: before.get(p.id)! } : p,
        ),
      );
      toast.error(messageOf(e));
    }
  };

  const orderPanel = (
    <TvOrderPanel
      posters={tvOrder(posters)}
      onReorder={handleReorder}
      slideSeconds={slideSeconds}
      onSlideSecondsChange={handleSlideSeconds}
      savingSeconds={savingSeconds}
      alertMinutes={alertMinutes}
      onAlertMinutesChange={handleAlertMinutes}
      savingAlerts={savingAlerts}
      loading={isPostersLoading}
    />
  );

  const tvPanels = (
    <>
      {orderPanel}
      {selectedOrgNumber && <DailyPostersPanel orgNr={selectedOrgNumber} />}
    </>
  );

  useEffect(() => {
    if (!showModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleCloseModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal]);

  const handleFileChange = (file: File | null) => {
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleEditFileChange = (file: File | null) => {
    if (file) {
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (values: PosterForm, { resetForm }: any) => {
    const currentTime = new Date();
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;

    const posterRequestDto = {
      date: formattedDateTime,
      tittel: values.tittel,
      tekst: values.tekst,
      mosqueOrgNr: selectedOrgNumber,
      activated: values.activated,
      activatedForTv: values.activatedForTv,
      priority: values.priority,
    };

    const formData = new FormData();
    if (values.plakat) formData.append("file", values.plakat);
    formData.append("posterRequestDto", JSON.stringify(posterRequestDto));

    try {
      const response = await execute(
        "/api/v1/poster/addWithImage",
        "POST",
        formData,
      );
      if (response) {
        setPosters((prevPosters) => [...prevPosters, response]);
        resetForm();
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.success("Plakaten er lagret.");
      } else {
        toast.error("Kunne ikke lagre plakaten. Prøv igjen.");
      }
    } catch (error: any) {
      console.error("Error:", error.response || error.message || error);
    }
  };

  const handlePosterClick = (poster: Poster) => {
    setSelectedPoster(poster);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPoster(null);
    setEditingPoster(null);
    setEditImagePreview(null);
  };

  const handleDeletePoster = async (posterToDelete: Poster) => {
    try {
      await execute(`/api/v1/poster/${posterToDelete.id}`, "DELETE");
      setPosters((prevPosters) =>
        prevPosters.filter((poster) => poster.id !== posterToDelete.id),
      );
      toast.success("Plakaten er slettet.");
    } catch (error: any) {
      console.error("Failed to delete poster", error);
    }
  };

  const confirmDeletePoster = async (poster: Poster) => {
    const ok = await confirm({
      title: "Slett plakat?",
      message: (
        <span>
          Plakaten <b>«{poster.tittel}»</b> fjernes fra appen og TV-skjermen.
          Dette kan ikke angres.
        </span>
      ),
      confirmLabel: "Slett plakat",
      tone: "danger",
    });
    if (ok) await handleDeletePoster(poster);
  };

  /** Velg hvilke TV-er plakaten vises på (tom = alle). Lagres straks. */
  const handleTvTargets = async (poster: Poster, ids: number[]) => {
    const setIds = (tvDeviceIds: number[] | undefined) =>
      setPosters((prev) =>
        prev.map((p) => (p.id === poster.id ? { ...p, tvDeviceIds } : p)),
      );
    setIds(ids);
    try {
      await api(
        `/api/v1/mosques/${selectedOrgNumber}/posters/${poster.id}/tv-devices`,
        "PUT",
        { tvDeviceIds: ids },
      );
      const names = tvs.filter((d) => ids.includes(d.id)).map((d) => d.name);
      toast.success(
        names.length
          ? `Vises på ${names.join(", ")}.`
          : "Vises på alle TV-ene.",
      );
    } catch (e) {
      setIds(poster.tvDeviceIds);
      toast.error(messageOf(e));
    }
  };

  /**
   * Slår «Vis i appen» (activated) eller «Vis på TV» (activatedForTv) av/på
   * direkte fra listen og lagrer straks. Ruller tilbake ved feil.
   */
  const handleToggle = async (
    poster: Poster,
    field: "activated" | "activatedForTv",
    on: boolean,
  ) => {
    const setValue = (value: boolean) =>
      setPosters((prev) =>
        prev.map((p) => (p.id === poster.id ? { ...p, [field]: value } : p)),
      );
    setValue(on);
    const formData = new FormData();
    formData.append(
      "posterRequestDto",
      JSON.stringify({
        date: posterStamp(poster.date),
        tittel: poster.tittel,
        tekst: poster.tekst ?? "",
        mosqueOrgNr: selectedOrgNumber,
        activated: poster.activated,
        activatedForTv: poster.activatedForTv,
        priority: poster.priority,
        [field]: on,
      }),
    );
    try {
      await api(
        `/api/v1/poster/${selectedOrgNumber}/${poster.id}`,
        "PATCH",
        formData,
      );
      toast.success(
        field === "activated"
          ? on
            ? "Plakaten vises i appen."
            : "Plakaten er skjult i appen."
          : on
            ? "Plakaten vises på TV."
            : "Plakaten er tatt av TV.",
      );
    } catch (e) {
      setValue(!on);
      toast.error(messageOf(e));
    }
  };

  const handleEditPoster = (poster: Poster) => {
    if (poster.design) {
      navigate(`${RoutePaths.TvStudio}?id=${poster.id}`);
      return;
    }
    setEditingPoster(poster);
    setShowModal(true);
    setEditImagePreview(poster.plakat);
  };

  const handleSaveEdit = async (values: PosterForm) => {
    if (!editingPoster) return;

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;

    const posterRequestDto = {
      date: formattedDateTime,
      tittel: values.tittel,
      tekst: values.tekst,
      mosqueOrgNr: selectedOrgNumber,
      activated: values.activated,
      activatedForTv: values.activatedForTv,
      priority: values.priority,
    };

    const formData = new FormData();
    formData.append("posterRequestDto", JSON.stringify(posterRequestDto));
    if (values.plakat) formData.append("file", values.plakat);

    try {
      await updatePoster(
        `/api/v1/poster/${selectedOrgNumber}/${editingPoster.id}`,
        "PATCH",
        formData,
      );
      setPosters((prev) =>
        prev.map((poster) =>
          poster.id === editingPoster.id
            ? {
                ...poster,
                tittel: values.tittel,
                tekst: values.tekst,
                activated: values.activated,
                activatedForTv: values.activatedForTv,
                plakat: editImagePreview || poster.plakat,
                priority: values.priority || 0,
              }
            : poster,
        ),
      );
      setShowModal(false);
      setEditingPoster(null);
      setEditImagePreview(null);
      toast.success("Endringene er lagret.");
    } catch (error) {
      console.error("Failed to update poster", error);
    }
  };

  const visiblePosters = posters.filter((poster) =>
    filter === "app"
      ? poster.activated
      : filter === "tv"
        ? poster.activatedForTv
        : true,
  );

  const formatDate = (date: string) => {
    const parsed = new Date(date);
    return isNaN(parsed.getTime())
      ? date
      : parsed.toLocaleDateString("nb-NO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };

  return (
    <>
      <PageHeader
        title={isAll ? "Plakater" : "Plakater på TV"}
        subtitle={
          isAll
            ? "Vises i appen og på TV"
            : "Plakater vises i appen og roterer på TV-skjermen i moskeen. Last opp et bilde eller lag plakaten med AI."
        }
        actions={
          <Button
            onClick={() => navigate(RoutePaths.TvStudio)}
            icon={<Icon name="spark" size={16} strokeWidth={2} />}
          >
            Lag plakat med AI
          </Button>
        }
      />
      {!isAll && <SectionTabs />}
      {!isAll && tvPanels}

      <div className={styles.layout}>
        <Card title="Last opp plakat">
          <Formik
            initialValues={{
              tittel: "",
              tekst: "",
              plakat: null,
              activated: true,
              activatedForTv: false,
              priority: 0,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty, setFieldValue, values }) => (
              <FormikForm className={styles.form} noValidate>
                <FormikTextField
                  name="tittel"
                  label="Tittel"
                  required
                  placeholder="Tittel på plakaten"
                />
                <FormikTextarea
                  name="tekst"
                  label="Tekst"
                  required
                  rows={3}
                  placeholder="Kort tekst om plakaten"
                />
                <ImageDrop
                  label="Plakatbilde"
                  required
                  preview={imagePreview}
                  inputRef={fileInputRef}
                  onFile={(file) => {
                    setFieldValue("plakat", file);
                    handleFileChange(file);
                  }}
                />
                <Select
                  label="Prioritet"
                  required
                  value={values.priority}
                  onChange={(e) =>
                    setFieldValue(
                      "priority",
                      parseInt(e.target.value || "50", 10),
                    )
                  }
                  help="Jo lavere tall, jo høyere opp. 1–100."
                >
                  {priorityOptions(values.priority).map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </Select>
                <Toggle
                  label={
                    <span className={styles.toggleLabel}>Vis i appen</span>
                  }
                  name="activated"
                  checked={values.activated}
                  onChange={(e) => setFieldValue("activated", e.target.checked)}
                />
                <Toggle
                  label={
                    <span className={styles.toggleLabel}>
                      Vis på TV-skjermen
                    </span>
                  }
                  name="activatedForTv"
                  checked={values.activatedForTv}
                  onChange={(e) =>
                    setFieldValue("activatedForTv", e.target.checked)
                  }
                />
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={!(isValid && dirty)}
                  icon={<Icon name="check" size={16} strokeWidth={2} />}
                >
                  {isLoading ? "Laster opp …" : "Lagre plakat"}
                </Button>
                {error && (
                  <Alert tone="error">Noe gikk galt. Prøv igjen senere.</Alert>
                )}
              </FormikForm>
            )}
          </Formik>
        </Card>

        <Card
          title="Alle plakater"
          actions={
            <SegmentedControl<PosterFilter>
              label="Vis plakater"
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: "Alle" },
                { value: "app", label: "App" },
                { value: "tv", label: "TV" },
              ]}
            />
          }
        >
          {isPostersLoading ? (
            <div className={ui.g2}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} height={240} radius={20} />
              ))}
            </div>
          ) : error ? (
            <Alert tone="error">Klarte ikke å hente plakatene.</Alert>
          ) : visiblePosters.length > 0 ? (
            <div className={ui.g2}>
              {visiblePosters.map((poster) => (
                <article key={poster.id} className={styles.poster}>
                  <span className={styles.posterImage}>
                    {poster.plakat && (
                      <img src={poster.plakat} alt={poster.tittel} />
                    )}
                    {poster.plakatPortrait && (
                      <img
                        className={styles.posterPortrait}
                        src={poster.plakatPortrait}
                        alt={`${poster.tittel}, stående`}
                        title="Stående utgave (plakatkarusellen på TV)"
                      />
                    )}
                  </span>
                  <div className={styles.posterBody}>
                    <div className={styles.posterTitleRow}>
                      <b className={styles.posterTitle}>{poster.tittel}</b>
                      <IconButton
                        label={
                          poster.design ? "Endre i «Lag plakat»" : "Rediger"
                        }
                        icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
                        onClick={() => handleEditPoster(poster)}
                      />
                      <IconButton
                        label="Slett"
                        icon={<Icon name="trash" size={17} strokeWidth={1.9} />}
                        onClick={() => confirmDeletePoster(poster)}
                      />
                    </div>
                    {poster.tekst && (
                      <span className={styles.posterText}>{poster.tekst}</span>
                    )}
                    <div className={styles.badges}>
                      {poster.design && (
                        <Badge tone="lilac">
                          <Icon name="spark" size={13} strokeWidth={2} />
                          Laget med AI
                        </Badge>
                      )}
                      {isAll ? null : poster.activated ? (
                        <Badge tone="mint">
                          <PosterIcon name="eye" size={13} />
                          App
                        </Badge>
                      ) : (
                        <Badge tone="grey">
                          <PosterIcon name="eyeOff" size={13} />
                          Skjult i app
                        </Badge>
                      )}
                      <Badge tone="gold">Prioritet {poster.priority}</Badge>
                      <span className={styles.posterDate}>
                        {formatDate(poster.date)}
                      </span>
                    </div>
                    {isAll && (
                      <Toggle
                        label={
                          <span className={styles.toggleLabel}>
                            Vis i appen
                          </span>
                        }
                        checked={poster.activated}
                        onChange={(e) =>
                          handleToggle(poster, "activated", e.target.checked)
                        }
                      />
                    )}
                    <Toggle
                      label={
                        <span className={styles.toggleLabel}>Vis på TV</span>
                      }
                      checked={poster.activatedForTv}
                      onChange={(e) =>
                        handleToggle(poster, "activatedForTv", e.target.checked)
                      }
                    />
                    {poster.activatedForTv && (
                      <TvTargetPicker
                        devices={tvs}
                        value={poster.tvDeviceIds}
                        label={`TV-er for «${poster.tittel}»`}
                        onChange={(ids) => handleTvTargets(poster, ids)}
                      />
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState icon={isAll ? "upload" : "tv"} title="Ingen plakater">
              {posters.length > 0
                ? "Ingen plakater passer filteret."
                : "Plakater du lagrer vises her."}
            </EmptyState>
          )}
        </Card>
      </div>

      {isAll && tvPanels}

      {showModal && editingPoster && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseModal();
          }}
        >
          <div
            className={ui.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-poster-title"
          >
            <Formik
              initialValues={{
                tittel: editingPoster.tittel,
                tekst: editingPoster.tekst,
                activated: editingPoster.activated,
                activatedForTv: editingPoster.activatedForTv,
                plakat: null,
                priority: editingPoster.priority,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSaveEdit}
            >
              {({ isValid, values, setFieldValue, isSubmitting }) => (
                <FormikForm noValidate>
                  <h2 className={ui.mh} id="edit-poster-title">
                    <span className={ui.mhIcon} aria-hidden="true">
                      <Icon name="edit" size={18} />
                    </span>
                    Rediger plakat
                    <IconButton
                      className={styles.modalClose}
                      label="Lukk"
                      icon={<Icon name="x" size={18} strokeWidth={2} />}
                      onClick={handleCloseModal}
                    />
                  </h2>
                  <div className={ui.mb}>
                    <FormikTextField
                      name="tittel"
                      label="Tittel"
                      required
                      placeholder="Tittel på plakaten"
                    />
                    <FormikTextarea
                      name="tekst"
                      label="Tekst"
                      required
                      rows={3}
                      placeholder="Kort tekst om plakaten"
                    />
                    <Select
                      label="Prioritet"
                      required
                      value={values.priority}
                      onChange={(e) =>
                        setFieldValue(
                          "priority",
                          parseInt(e.target.value || "50", 10),
                        )
                      }
                      help="Jo lavere tall, jo høyere opp. 1–100."
                    >
                      {priorityOptions(values.priority).map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </Select>
                    <div className={styles.toggles}>
                      <Toggle
                        label={
                          <span className={styles.toggleLabel}>
                            Vis i appen
                          </span>
                        }
                        name="activated"
                        checked={values.activated}
                        onChange={(e) =>
                          setFieldValue("activated", e.target.checked)
                        }
                      />
                      <Toggle
                        label={
                          <span className={styles.toggleLabel}>
                            Vis på TV-skjermen
                          </span>
                        }
                        name="activatedForTv"
                        checked={values.activatedForTv}
                        onChange={(e) =>
                          setFieldValue("activatedForTv", e.target.checked)
                        }
                      />
                    </div>
                    <ImageDrop
                      label="Bytt bilde"
                      preview={editImagePreview}
                      onFile={(file) => {
                        setFieldValue("plakat", file);
                        handleEditFileChange(file);
                      }}
                    />
                  </div>
                  <div className={ui.mf}>
                    <Button variant="secondary" onClick={handleCloseModal}>
                      Avbryt
                    </Button>
                    <Button
                      type="submit"
                      disabled={!isValid}
                      loading={isSubmitting}
                    >
                      Lagre endringer
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        </div>
      )}
    </>
  );
};

export default PosterPage;
